/**
 * SendReportEmailDialog — Dërgon raport si PDF me email.
 *
 * Rrjedha:
 *  1. Gjeneron PDF me jsPDF (programatikisht, pa hapur dritare)
 *  2. Nëse REACT_APP_NOTIFICATIONS_URL është aktiv  → dërgon email me PDF bashkëngjitur direkt
 *  3. Fallback (API jo aktiv / jo i konfiguruar)    → shkarkon PDF + hap mailto:
 *
 * Props: open, onClose, reportTitle, subtitle, columns, rows, totals, workers,
 *        company, orientation
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Chip, Typography, Box,
  Alert, LinearProgress, Divider, Autocomplete,
  CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import EmailIcon        from '@mui/icons-material/Email';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon         from '@mui/icons-material/Send';
import CloseIcon        from '@mui/icons-material/Close';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import notificationsService from '../../services/notificationsService';
import config from '../../config/config';

const BRAND = '#1D4ED8';

/* ── Formatim vlerash ──────────────────────────────────────────────────────── */
function fmtVal(value, format) {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'currency') {
    const n = Number(value);
    return Number.isFinite(n)
      ? new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n)
      : String(value);
  }
  if (format === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString('sq-AL') : String(value);
  }
  return String(value);
}

function getCellVal(col, row) {
  if (typeof col.key === 'function') return col.key(row);
  return row[col.key];
}

/* ── Gjenero PDF me jsPDF (pa autoTable) ──────────────────────────────────── */
async function buildPdfBase64({ title, subtitle, company, orientation = 'landscape', columns, rows, totals }) {
  const { jsPDF } = await import('jspdf');

  const isLand = orientation !== 'portrait';
  const doc = new jsPDF({ orientation: isLand ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });

  const pageW  = doc.internal.pageSize.getWidth();
  const pageH  = doc.internal.pageSize.getHeight();
  const margin = 10;
  const cW     = pageW - 2 * margin;

  /* Gjerësia e kolonave — proporcionale */
  const colWidths = columns.map(c => c.pdfWidth || (cW / columns.length));
  const totalColW = colWidths.reduce((a, b) => a + b, 0);
  /* Normalizoje nëse totali nuk përputhet */
  const scaleW = cW / totalColW;
  const cws    = colWidths.map(w => w * scaleW);

  let y = margin;
  const hdrH   = 14;
  const metaH  = 7;
  const thH    = 7;
  const rowH   = 6.5;
  const totH   = 7;
  const font   = 'helvetica';

  /* ── Header gradient simulation (dy rektangula) ── */
  doc.setFillColor(30, 58, 95);
  doc.rect(margin, y, cW, hdrH, 'F');

  doc.setFont(font, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.text(title, margin + 3, y + 9);

  if (company) {
    doc.setFont(font, 'normal');
    doc.setFontSize(6.5);
    doc.text(company.toUpperCase(), margin + 3, y + 4);
  }
  if (subtitle) {
    doc.setFontSize(8);
    doc.text(subtitle, margin + 3, y + 13);
  }

  /* Data + rekorde – këndi i djathtë */
  const now = new Date().toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  doc.setFontSize(7);
  doc.text(`${now}  |  ${rows.length} rekorde`, pageW - margin - 3, y + 7, { align: 'right' });
  y += hdrH;

  /* ── Meta bar ── */
  doc.setFillColor(239, 246, 255);
  doc.rect(margin, y, cW, metaH, 'F');
  doc.setDrawColor(191, 219, 254);
  doc.rect(margin, y, cW, metaH, 'S');
  doc.setFont(font, 'normal');
  doc.setTextColor(30, 64, 175);
  doc.setFontSize(7);
  doc.text(`Gjeneruar nga Nexuma  |  ${title}${subtitle ? '  —  ' + subtitle : ''}`, margin + 3, y + 4.5);
  y += metaH + 1;

  /* ── Table header ── */
  const drawTableHeader = (yPos) => {
    doc.setFillColor(29, 78, 216);
    doc.rect(margin, yPos, cW, thH, 'F');
    doc.setFont(font, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    let x = margin;
    columns.forEach((col, i) => {
      const align = col.align === 'right' ? 'right' : 'left';
      const tx    = align === 'right' ? x + cws[i] - 1.5 : x + 1.5;
      const lbl   = col.label.length > 18 ? col.label.slice(0, 17) + '…' : col.label;
      doc.text(lbl, tx, yPos + 5, { align });
      x += cws[i];
    });
  };

  drawTableHeader(y);
  y += thH;

  /* ── Rreshtat ── */
  doc.setFont(font, 'normal');
  doc.setFontSize(7);

  rows.forEach((row, ri) => {
    if (y + rowH > pageH - margin - 10) {
      doc.addPage();
      y = margin;
      drawTableHeader(y);
      y += thH;
    }

    if (ri % 2 === 1) {
      doc.setFillColor(248, 250, 255);
      doc.rect(margin, y, cW, rowH, 'F');
    } else {
      doc.setFillColor(255, 255, 255);
      doc.rect(margin, y, cW, rowH, 'F');
    }

    doc.setDrawColor(232, 237, 245);
    doc.line(margin, y + rowH, margin + cW, y + rowH);

    doc.setTextColor(26, 26, 46);
    let x = margin;
    columns.forEach((col, i) => {
      const raw     = getCellVal(col, row);
      const val     = fmtVal(raw, col.format);
      const align   = col.align === 'right' ? 'right' : 'left';
      const tx      = align === 'right' ? x + cws[i] - 1.5 : x + 1.5;
      const maxLen  = Math.floor(cws[i] / 1.8);
      const display = val.length > maxLen ? val.slice(0, maxLen - 1) + '…' : val;
      doc.text(display, tx, y + 4.5, { align });
      x += cws[i];
    });

    y += rowH;
  });

  /* ── Totali ── */
  if (totals) {
    if (y + totH > pageH - margin - 8) {
      doc.addPage();
      y = margin;
    }
    doc.setFillColor(29, 78, 216);
    doc.rect(margin, y, cW, totH, 'F');
    doc.setFont(font, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7.5);
    let x = margin;
    columns.forEach((col, i) => {
      const align = col.align === 'right' ? 'right' : 'left';
      const tx    = align === 'right' ? x + cws[i] - 1.5 : x + 1.5;
      if (i === 0) {
        doc.text('TOTALI', tx, y + 5, { align: 'left' });
      } else {
        const tKey = col.totalKey || (typeof col.key === 'string' ? col.key : null);
        const val  = tKey != null ? totals[tKey] : undefined;
        if (val !== undefined) doc.text(fmtVal(val, col.format), tx, y + 5, { align });
      }
      x += cws[i];
    });
    y += totH;
  }

  /* ── Footer ── */
  const fy = pageH - margin + 2;
  doc.setFont(font, 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(150, 150, 150);
  doc.text('Nexuma · Raport konfidencial', margin, fy);
  doc.text(`Gjeneruar: ${new Date().toLocaleString('sq-AL')}`, pageW - margin, fy, { align: 'right' });

  return doc.output('datauristring'); // data:application/pdf;base64,...
}

/* ── Shkarko PDF direkt ────────────────────────────────────────────────────── */
function downloadPdf(dataUri, filename) {
  const link = document.createElement('a');
  link.href = dataUri;
  link.download = `${filename || 'raport'}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/* ── Main component ────────────────────────────────────────────────────────── */
export default function SendReportEmailDialog({
  open, onClose,
  reportTitle  = 'Raport',
  subtitle     = '',
  columns      = [],
  rows         = [],
  totals       = null,
  workers      = [],
  company      = '',
  orientation  = 'landscape',
}) {
  const [recipients, setRecipients] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [subject,    setSubject]    = useState('');
  const [message,    setMessage]    = useState('');
  const [sending,    setSending]    = useState(false);
  const [status,     setStatus]     = useState(null);
  const [statusMsg,  setStatusMsg]  = useState('');
  const [companyName,setCompanyName]= useState(company || 'Nexuma');

  const hasApi = Boolean(config?.notificationsApiUrl);

  /* reset on open */
  useEffect(() => {
    if (open) {
      setSubject(`${reportTitle}${subtitle ? ' — ' + subtitle : ''}`);
      setMessage(''); setStatus(null); setStatusMsg('');
      setSending(false); setInputValue('');
    }
  }, [open, reportTitle, subtitle]);

  /* ngarko emrin e kompanisë */
  useEffect(() => {
    if (company) { setCompanyName(company); return; }
    import('../../services/apiServices').then(m => {
      m.default().get('settings').then(r => {
        if (r?.success && r.data?.[0]?.CompanyName) setCompanyName(r.data[0].CompanyName);
      }).catch(() => {});
    }).catch(() => {});
  }, [company]);

  /* opsionet autocomplete */
  const workerOptions = useMemo(() => workers
    .filter(w => w.Email)
    .map(w => ({
      label: `${w.FullName || (`${w.FirstName || ''} ${w.LastName || ''}`).trim()} <${w.Email}>`,
      email: w.Email,
    })), [workers]);

  /* shto email */
  const addEmail = (email) => {
    if (!email) return;
    const t = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return;
    if (!recipients.includes(t)) setRecipients(p => [...p, t]);
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (['Enter', ',', 'Tab', ' '].includes(e.key)) { e.preventDefault(); addEmail(inputValue); }
  };

  /* ── Dërgo ── */
  const handleSend = async () => {
    if (recipients.length === 0) {
      setStatusMsg('Shtoni të paktën një adresë emaili.');
      return;
    }
    setStatusMsg(''); setSending(true); setStatus(null);

    let pdfDataUri;
    try {
      pdfDataUri = await buildPdfBase64({
        title: reportTitle, subtitle, company: companyName,
        orientation, columns, rows, totals,
      });
    } catch (err) {
      console.error('PDF generation error:', err);
      setSending(false);
      setStatus('error');
      setStatusMsg('Gjenerimi i PDF dështoi. Provoni sërisht.');
      return;
    }

    const base64 = pdfDataUri.split(',')[1];
    const filename = `${reportTitle.replace(/[^a-zA-Z0-9\-_]/g, '_')}.pdf`;

    if (hasApi) {
      try {
        await notificationsService.sendEmail({
          to: recipients,
          subject,
          html: `<p>${message ? message + '<br><br>' : ''}<strong>${reportTitle}</strong>${subtitle ? ' — ' + subtitle : ''}<br>${rows.length} rekorde.<br><br>PDF i bashkëngjitur.</p>`,
          text: `${reportTitle}${subtitle ? ' — ' + subtitle : ''}\n${message || ''}\n\nPDF i bashkëngjitur.\n${rows.length} rekorde.`,
          attachments: [{
            filename,
            content: base64,
            encoding: 'base64',
            contentType: 'application/pdf',
          }],
        });
        setSending(false);
        setStatus('success');
        setStatusMsg(`Emaili me PDF u dërgua tek ${recipients.length} marrës!`);
        return;
      } catch {
        /* API dështoi → fallback: shkarko + mailto */
      }
    }

    /* Fallback: shkarko PDF + hap mailto */
    downloadPdf(pdfDataUri, reportTitle);

    const body = `${reportTitle}${subtitle ? '\n' + subtitle : ''}\n\n${message ? message + '\n\n' : ''}PDF u shkarkua automatikisht — bashkëngjithe në email.\n\n${rows.length} rekorde.`;
    const mailto = `mailto:${recipients.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, '_blank');

    setSending(false);
    setStatus('fallback');
    setStatusMsg('PDF u shkarkua. Hapet klienti i emailit — bashkëngjithe PDF-in dhe dërgoje.');
  };

  const canSend = recipients.length > 0 && !sending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>

      {/* ── Titulli ── */}
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ bgcolor: BRAND, color: 'white', p: 0.75, borderRadius: 1, display: 'flex' }}>
              <EmailIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Dërgo PDF me Email
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {reportTitle}{subtitle ? ` — ${subtitle}` : ''}
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>

        {/* ── Statusi ── */}
        {status === 'success'  && <Alert icon={<CheckCircleIcon />} severity="success" sx={{ mb: 2 }}>{statusMsg}</Alert>}
        {status === 'fallback' && <Alert severity="info"    sx={{ mb: 2 }}>{statusMsg}</Alert>}
        {status === 'error'    && <Alert severity="error"   sx={{ mb: 2 }}>{statusMsg}</Alert>}
        {statusMsg && !status  && <Alert severity="warning" sx={{ mb: 2 }}>{statusMsg}</Alert>}

        {/* ── Info box ── */}
        <Box sx={{ bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 1.5, p: 1.5, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <PictureAsPdfIcon sx={{ color: BRAND, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>PDF gjenerohet automatikisht dhe dërgohet me email</Typography>
            <Typography variant="caption" color="text.secondary">
              {rows.length} rreshta · {columns.length} kolona{totals ? ' · Me totale' : ''}
            </Typography>
          </Box>
        </Box>

        {/* ── Marrësit ── */}
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Marrësit *
        </Typography>
        <Box
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 2, minHeight: 48, cursor: 'text', '&:focus-within': { borderColor: BRAND } }}
          onClick={() => document.getElementById('pdf-email-input')?.focus()}
        >
          <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
            {recipients.map(e => (
              <Chip key={e} label={e} size="small" onDelete={() => setRecipients(p => p.filter(x => x !== e))} />
            ))}
            <Autocomplete
              freeSolo options={workerOptions}
              inputValue={inputValue}
              onInputChange={(_, v) => setInputValue(v)}
              onChange={(_, opt) => {
                if (opt && typeof opt === 'object') addEmail(opt.email);
                else if (typeof opt === 'string') addEmail(opt);
              }}
              renderInput={params => (
                <TextField {...params} id="pdf-email-input"
                  placeholder={recipients.length === 0 ? 'email@example.com, ...' : ''}
                  variant="standard"
                  InputProps={{ ...params.InputProps, disableUnderline: true, sx: { fontSize: '0.85rem' } }}
                  onKeyDown={handleKeyDown}
                  onBlur={() => addEmail(inputValue)}
                  sx={{ minWidth: 180, flex: 1 }}
                />
              )}
              sx={{ flex: 1, '& .MuiAutocomplete-popupIndicator': { display: 'none' } }}
            />
          </Stack>
        </Box>

        {/* ── Subjekti ── */}
        <TextField
          fullWidth label="Subjekti" value={subject}
          onChange={e => setSubject(e.target.value)} size="small" sx={{ mb: 2 }}
        />

        {/* ── Mesazhi ── */}
        <TextField
          fullWidth label="Mesazh (opsional)" value={message}
          onChange={e => setMessage(e.target.value)}
          multiline rows={2} size="small"
          placeholder="Shto tekst personal para PDF-it..."
          sx={{ mb: 1 }}
        />

        {sending && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Duke gjeneruar PDF dhe dërguar email…
            </Typography>
            <LinearProgress sx={{ borderRadius: 1 }} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        <Tooltip title={hasApi ? '' : 'API nuk është konfiguruar — do të shkarkohet PDF dhe hapet email client'}>
          <span>
            <Button onClick={onClose} disabled={sending}>Mbyll</Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={!canSend}
          sx={{ minWidth: 160, bgcolor: BRAND }}
        >
          {sending ? 'Duke dërguar…' : hasApi ? 'Gjenero PDF & Dërgo' : 'Gjenero PDF & Hap Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
